/**
 * Contractor Documents Page (read-only repository)
 * Shows the documents, portfolio evidence, and references submitted for Eqaya verification.
 * No add / edit / upload controls — editing happens via the Verification flow only.
 */

import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Paper,
    Breadcrumbs,
    Link,
    Card,
    CardContent,
    Grid,
    Stack,
    Box,
    Chip,
    CircularProgress,
    Alert,
    Divider
} from '@mui/material';
import {
    Home,
    Description,
    CheckCircle,
    Cancel
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import contractorVerificationService from '../services/contractorVerificationService';

const documentTypeLabels = {
    company_registration: 'Company registration',
    director_id: 'Owner/director ID',
    tax_clearance: 'ZIMRA tax clearance',
    nssa_registration: 'NSSA registration',
    praz_registration: 'PRAZ registration',
    industry_membership: 'CIFOZ/ZBCA/ZCIA/NECCIZ membership',
    bank_confirmation: 'Bank confirmation',
    insurance: 'Insurance',
    trade_certificate: 'Trade certificate',
    other: 'Other'
};

const statusLabel = (value = '') => String(value || '').replace(/_/g, ' ');

const StatusChip = ({ status }) => {
    const colorMap = {
        approved: 'success',
        approved_with_limits: 'success',
        submitted: 'info',
        under_review: 'info',
        needs_more_info: 'warning',
        rejected: 'error',
        suspended: 'error',
        revoked: 'error',
        expired: 'default',
        draft: 'default',
        not_started: 'default',
        pending: 'default'
    };
    return (
        <Chip
            size="small"
            label={statusLabel(status || 'pending')}
            color={colorMap[status] || 'default'}
        />
    );
};

const EvidenceCard = ({ primary, secondary, status }) => (
    <Box
        sx={{
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.default'
        }}
    >
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
            <Box>
                <Typography variant="body2" fontWeight={700}>{primary}</Typography>
                {secondary && (
                    <Typography variant="caption" color="text.secondary">{secondary}</Typography>
                )}
            </Box>
            <StatusChip status={status} />
        </Stack>
    </Box>
);

const ContractorDocumentsPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [application, setApplication] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await contractorVerificationService.getMyVerification();
                setApplication(response.data);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to load verification data');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const documents = application?.documents || [];
    const portfolioEvidence = application?.portfolioEvidence || [];
    const references = application?.references || [];

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 3 }}>
                <Link
                    color="inherit"
                    href="#"
                    onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                >
                    <Home sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </Link>
                <Typography
                    sx={{ display: 'flex', alignItems: 'center' }}
                    color="text.primary"
                >
                    <Description sx={{ mr: 0.5 }} fontSize="inherit" />
                    My Documents
                </Typography>
            </Breadcrumbs>

            {/* Page Header */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h4" gutterBottom>
                    My Submitted Documents
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    These are the documents you submitted for verification. To make changes, use the Contractor Verification flow.
                </Typography>
                {application && (
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <StatusChip status={application.status} />
                        {application.badge_type && application.badge_type !== 'none' && (
                            <Chip size="small" color="success" label={statusLabel(application.badge_type)} />
                        )}
                    </Stack>
                )}
            </Paper>

            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography>Loading your documents...</Typography>
                </Box>
            )}

            {!loading && error && (
                <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            )}

            {!loading && !error && application && (
                <Stack spacing={3}>
                    {/* Required Documents */}
                    <Card>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>
                                    Verification Documents
                                </Typography>
                                <Chip
                                    size="small"
                                    label={`${documents.length} document${documents.length !== 1 ? 's' : ''}`}
                                    color={documents.length >= 3 ? 'success' : 'default'}
                                />
                            </Stack>
                            <Divider sx={{ mb: 2 }} />
                            {documents.length === 0 ? (
                                <Alert severity="warning" variant="outlined">No verification documents on file.</Alert>
                            ) : (
                                <Stack spacing={1.25}>
                                    {documents.map((doc) => (
                                        <EvidenceCard
                                            key={doc.id}
                                            primary={documentTypeLabels[doc.document_type] || doc.document_name || doc.document_type}
                                            secondary={[doc.document_name, doc.reference_number].filter(Boolean).join(' • ') || undefined}
                                            status={doc.status}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>

                    {/* Portfolio Evidence + References side by side on md+ */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                        <Box>
                                            <Typography variant="h6" fontWeight={700}>Portfolio Evidence</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Completed projects submitted for Eqaya to verify.
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            {portfolioEvidence.length >= 2
                                                ? <CheckCircle color="success" fontSize="small" />
                                                : <Cancel color="error" fontSize="small" />}
                                            <Chip
                                                size="small"
                                                label={`${Math.min(portfolioEvidence.length, 2)} / 2`}
                                                color={portfolioEvidence.length >= 2 ? 'success' : 'error'}
                                            />
                                        </Stack>
                                    </Stack>
                                    <Divider sx={{ mb: 2 }} />
                                    {portfolioEvidence.length === 0 ? (
                                        <Alert severity="warning" variant="outlined">No portfolio evidence on file.</Alert>
                                    ) : (
                                        <Stack spacing={1.25}>
                                            {portfolioEvidence.map((item) => (
                                                <EvidenceCard
                                                    key={item.id}
                                                    primary={item.project_title}
                                                    secondary={[item.project_location, item.client_name].filter(Boolean).join(' • ') || undefined}
                                                    status={item.status}
                                                />
                                            ))}
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                        <Box>
                                            <Typography variant="h6" fontWeight={700}>References</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Client or trade references submitted for Eqaya review.
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            {references.length >= 2
                                                ? <CheckCircle color="success" fontSize="small" />
                                                : <Cancel color="error" fontSize="small" />}
                                            <Chip
                                                size="small"
                                                label={`${Math.min(references.length, 2)} / 2`}
                                                color={references.length >= 2 ? 'success' : 'error'}
                                            />
                                        </Stack>
                                    </Stack>
                                    <Divider sx={{ mb: 2 }} />
                                    {references.length === 0 ? (
                                        <Alert severity="warning" variant="outlined">No references on file.</Alert>
                                    ) : (
                                        <Stack spacing={1.25}>
                                            {references.map((ref) => (
                                                <EvidenceCard
                                                    key={ref.id}
                                                    primary={ref.referee_name}
                                                    secondary={[ref.relationship, ref.project_title].filter(Boolean).join(' • ') || undefined}
                                                    status={ref.status}
                                                />
                                            ))}
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Stack>
            )}
        </Container>
    );
};

export default ContractorDocumentsPage;
